import { IssueTypeValues } from '@issuemanager/constants';
import { z } from 'zod';

// 1-to-1 mapping with database columns
export const rawIssueTypeSchema = z.object({
  id: z.string(),
  created_at: z.number().transform((date) => new Date(date).toISOString()),
  updated_at: z.number().transform((date) => new Date(date).toISOString()),
  issue_type: z.enum(IssueTypeValues),
  organization_id: z.string(),
  deleted_at: z
    .number()
    .transform((date) => new Date(date).toISOString())
    .nullable(),
  created_by: z.string().nullable(),
});

export type RawIssueTypeSchema = z.infer<typeof rawIssueTypeSchema>;
// Common transformer for converting IssueType columns from snake_case to camelCase
const issueTypeSchemaTransformer = (issueType: RawIssueTypeSchema) => ({
  id: issueType.id,
  name: issueType.issue_type,
  organizationId: issueType.organization_id,
  createdAt: issueType.created_at,
  updatedAt: issueType.updated_at,
  deletedAt: issueType.deleted_at,
  createdBy: issueType.created_by,
});

export const issueTypeSchema = rawIssueTypeSchema.transform(
  issueTypeSchemaTransformer,
);
export const extendedIssueTypeSchema = rawIssueTypeSchema
  .extend({
    hasIssueSubtypes: z.boolean(),
  })
  .transform((issueType) => ({
    ...issueTypeSchemaTransformer(issueType),
    hasIssueSubtypes: issueType.hasIssueSubtypes,
  }));
export type ExtendedIssueTypeEntity = z.infer<typeof extendedIssueTypeSchema>;

export type IssueTypeEntity = z.infer<typeof issueTypeSchema>;

export type IssueType = Omit<
  IssueTypeEntity,
  'id' | 'createdAt' | 'updatedAt' | 'deletedAt'
>;

// Minimal issue type schema intended to be used for validating joined entities
export const minimalIssueTypeSchema = rawIssueTypeSchema
  .pick({
    id: true,
    issue_type: true,
  })
  .transform((issueType) => ({
    id: issueType.id,
    name: issueType.issue_type,
  }));

export type MinimalIssueTypeEntity = z.infer<typeof minimalIssueTypeSchema>;
